// Google review pop-up and bubble.
// - The full review card opens by itself once, after `data-delay` seconds of the page being visible, unless another dialog is open. Closing it is remembered for `data-days` days, so it does not reappear on every page.
// - A small bubble (revealed by an inline script in the snippet as soon as the shopper scrolls) stays pinned bottom-left on every page. Clicking it reopens the card; its cross hides the bubble until the browser or tab is closed.
// - Using the review link or proof email retires both for `data-days` days.
// - Adding ?review-test to any page address clears the memory on that device (for testing).
// In the theme editor (`data-preview`) the memory is ignored so both can be previewed.
const POPUP_KEY = 'sweetzone:review-popup-hidden-until';
// The bubble is only hidden for the rest of the current visit (until the browser or tab is closed), so it keeps showing on later visits.
const BUBBLE_SESSION_KEY = 'sweetzone:review-bubble-closed';
const DAY_MS = 24 * 60 * 60 * 1000;

class ReviewPopup extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    this.bubble = this.querySelector('[data-review-bubble]');
    if (!this.dialog) return;

    this.preview = this.hasAttribute('data-preview');
    // Visiting any page with ?review-test in the address clears this device's memory, so the bubble and card can be checked on a phone that dismissed them earlier.
    if (new URLSearchParams(location.search).has('review-test')) {
      try {
        localStorage.removeItem(POPUP_KEY);
        sessionStorage.removeItem(BUBBLE_SESSION_KEY);
      } catch {}
    }
    this.delayMs = Math.max(1, Number(this.dataset.delay) || 15) * 1000;
    this.days = Math.max(1, Number(this.dataset.days) || 7);

    this.dialog.addEventListener('close', () => this.remember(POPUP_KEY), { signal });
    // Clicking the dimmed backdrop closes the card.
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.dialog.close();
      },
      { signal },
    );
    // Taking an action closes the card and retires the bubble too.
    for (const action of this.querySelectorAll('[data-review-action]')) {
      action.addEventListener(
        'click',
        () =>
          setTimeout(() => {
            this.dialog.close();
            this.hideBubble();
          }, 300),
        { signal },
      );
    }

    if (this.bubble) {
      this.querySelector('[data-review-open]')?.addEventListener('click', () => this.open(), { signal });
      this.querySelector('[data-review-dismiss]')?.addEventListener('click', () => this.hideBubble(), { signal });
    }

    // Automatic pop-up after the delay, counting only time the page is visible.
    if (this.preview || !this.isHidden(POPUP_KEY)) {
      this.elapsed = 0;
      this.popupTimer = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        this.elapsed += 500;
        if (this.elapsed < this.delayMs) return;
        // Wait politely if another dialog (cart drawer, checkout pop-up, menu) is open.
        if (document.querySelector('dialog[open]')) return;
        clearInterval(this.popupTimer);
        this.open();
      }, 500);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearInterval(this.popupTimer);
  }

  open() {
    clearInterval(this.popupTimer);
    if (!this.dialog.open && typeof this.dialog.showModal === 'function') this.dialog.showModal();
  }

  hideBubble() {
    if (!this.preview) {
      try {
        sessionStorage.setItem(BUBBLE_SESSION_KEY, '1');
      } catch {}
    }
    if (!this.bubble || this.bubble.hidden) return;
    const hide = () => {
      this.bubble.hidden = true;
      this.bubble.classList.remove('is-leaving');
    };
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      this.bubble.classList.add('is-leaving');
      this.bubble.addEventListener('animationend', hide, { once: true });
      setTimeout(hide, 400);
    } else {
      hide();
    }
  }

  isHidden(key) {
    try {
      return Number(localStorage.getItem(key) || 0) > Date.now();
    } catch {
      return false;
    }
  }

  remember(key) {
    if (this.preview) return;
    try {
      localStorage.setItem(key, String(Date.now() + this.days * DAY_MS));
    } catch {
      // Storage unavailable (private mode); it may show again next visit.
    }
  }
}

if (!customElements.get('s-review-popup')) customElements.define('s-review-popup', ReviewPopup);
